import { CONTRACTS_ENDPOINT, TENANT_SETTINGS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { Contract } from "../../../../types/contracts";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Chip, Typography, Divider } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { useHasPermission } from "../../../../contexts/auth";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

type ContractBasicInfoProps = {
  previewId?: string | number;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

export const ContractBasicInfo = ({ previewId }: ContractBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(CONTRACTS_ENDPOINT);
  const { get: getTenantSettings } = useCRUD(TENANT_SETTINGS_ENDPOINT);
  const [contract, setContract] = useState<Contract | null>(null);
  const [automationRunDay, setAutomationRunDay] = useState<number | null>(null);
  const canViewClients = useHasPermission("settlement.view_client");
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchContract = async () => {
      if (entityId) {
        const response = await get(entityId);
        setContract(response.data);
      }
    };

    const handleContractUpdated = () => {
      fetchContract();
    };

    window.addEventListener("contractUpdated", handleContractUpdated);
    fetchContract();
    return () => {
      window.removeEventListener("contractUpdated", handleContractUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => {
    const fetchAutomationRunDay = async () => {
      const response = await getTenantSettings("settings");
      setAutomationRunDay(response?.data?.automation_run_day ?? null);
    };

    fetchAutomationRunDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!contract) return <CustomCircularProgress />;

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {contract.internal_name || contract.number}
        </Typography>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {contract.client && (
            <DetailRow label={t("Client Name")}>
              <EntityReferenceLink
                entityType="client"
                entityId={contract.client?.id}
                label={contract.client?.short_name || contract.client?.name || "-"}
                canPreview={canViewClients}
                stopPropagation={false}
              />
            </DetailRow>
          )}
          {contract.internal_name && (
            <DetailRow label={t("Internal name")}>
              <Typography variant="subtitle1">{contract.internal_name}</Typography>
            </DetailRow>
          )}
          {contract.number && (
            <DetailRow label={t("Number")}>
              <Typography variant="subtitle1">{contract.number}</Typography>
            </DetailRow>
          )}
          {contract.numeration_schema && (
            <DetailRow label={t("Numeration schema")}>
              <Typography variant="subtitle1">{contract?.numeration_schema?.name || "-"}</Typography>
            </DetailRow>
          )}
          <DetailRow label={t("Settlement kind")}>
            <Typography variant="subtitle1">
              {contract.settlement_kinds?.join(", ") || "-"}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Has pending settlements")}>
            <Chip
              label={contract.has_pending_settlements ? t("Unsettled") : t("Settled")}
              size="small"
              sx={{
                bgcolor: contract.has_pending_settlements
                  ? "warning.light"
                  : "success.light",
                color: contract.has_pending_settlements
                  ? "warning.contrastText"
                  : "success.contrastText",
                fontWeight: 600,
                "& .MuiChip-label": {
                  color: contract.has_pending_settlements
                    ? "warning.contrastText"
                    : "success.contrastText",
                },
              }}
            />
          </DetailRow>
          <DetailRow label={t("Inflation condition")}>
            <Typography variant="subtitle1">
              {contract.inflation_condition_display?.trim()
                ? `${contract.inflation_condition_display.trim()}${
                    contract.inflation_rate_percent != null
                      ? ` (${contract.inflation_rate_percent}%)`
                      : ""
                  }`
                : "-"}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Settlement Automation")} noBorder>
            <Typography variant="subtitle1">
              {contract.automation_level_display?.trim() || t("Manual")}
              {contract.automation_level && automationRunDay
                ? ` ${t("(runs on day {{day}} of each month)", { day: automationRunDay })}`
                : ""}
            </Typography>
          </DetailRow>
        </Box>
      </Box>

      <Divider />

      {/* Contract Dates Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Contract Dates")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {contract.start_date && (
            <Typography variant="subtitle1">
              <strong>{t("Start Date")}:</strong> {contract.start_date}
            </Typography>
          )}
          <Typography variant="subtitle1">
            <strong>{t("End Date")}:</strong>{" "}
            {contract.end_date || t("Indefinite")}
          </Typography>
          {contract.issuance_date && (
            <Typography variant="subtitle1">
              <strong>{t("Issuance Date")}:</strong> {contract.issuance_date}
            </Typography>
          )}
          {contract.signature_date && (
            <Typography variant="subtitle1">
              <strong>{t("Signature Date")}:</strong> {contract.signature_date}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
